import { TimePicker } from '@progress/kendo-react-dateinputs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import DateInput from 'components/DateInput'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import { useEffect } from 'react'
import { cloneElement } from 'react'
import { useState } from 'react'
import { LocationItem } from 'types/location'
import { classNames } from 'utils/dom-helpers'
import { getTranslation } from 'utils/language-helper'
import { ServiceApiTypeEnum } from '../../../../types/service'
import { StaffAvailability } from '../../../../types/staff'
import { addHours, toYmd } from '../../../../utils/string-localization-helper'
import ServiceAvailSelector from '../../../DropdownSelectors/ServiceAvailabilitySelector'
import { DayOfWeek } from '../../types'
import getConfig from 'next/config'

const MAX_TIME_RANGE = new Date("3001-01-01 23:58")
const MAX_TIME = new Date("3001-01-01 23:59")

export default function OverrideRowForm({
    override,
    idx,
    onAttributesChanged,
    removeHandler
}: {
    override: any
    idx: number
    onAttributesChanged(override: any, idx: number): void
    removeHandler(idx: number, staff_availability: StaffAvailability): void
}) {
    const { translations } = useAppContext()
    const { services, locations } = useTenantContext()
    const appointments = services
        .filter((item) => item?.service_type === ServiceApiTypeEnum.APPOINTMENT)
    const [spaces, setSpaces] = useState<any>([])
    const [location_options, setLocationOptions] = useState<LocationItem[]>(locations)
    const [selected_spaces, setSelectedSpaces] = useState<any>([])
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    const AvailabilityOptions = [
        {
            text: getTranslation(
                'staff.available',
                translations
            ),
            value: getTranslation(
                'staff.available',
                translations
            ),
            image_url: `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/availability/ico-circle-green.svg`
        },
        {
            text: getTranslation(
                'staff.block',
                translations
            ),
            value: getTranslation(
                'staff.block',
                translations
            ),
            image_url: `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/availability/ico-circle-red.svg`
        },
    ]

    useEffect(() => {
        if (locations) {
            setLocationOptions([{ id: "", name: "All Locations" } as LocationItem, ...locations])
            const spaces_cache = locations.filter((loc: any) => loc?.id === override?.location)?.[0]?.location_spaces
                ?.map((loc_space: any) => {
                    return ({
                        id: loc_space.id,
                        name: loc_space.name
                    })
                })
            setSpaces(spaces_cache)

            let loc_spaces: any[] = []
            if (override?.location_space) {
                override.location_space.map((loc_space: string) => {
                    const selected_space = spaces_cache?.filter((space: any) => space?.id === loc_space)
                    loc_spaces.push(selected_space?.[0])
                })
            }
            setSelectedSpaces(loc_spaces)
        }
    }, [locations, override])

    function AvailValueRender(
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) {
        const children = [
            <div className="flex items-center" key={value[0]?.value}>
                <img
                    className="absolute"
                    src={value[0]?.image_url}
                    alt="static"
                />
                <div key={1} className="pl-4">
                    {value ? value[0]?.text : 'Available'}
                </div>
            </div>,
        ]

        return cloneElement(element, { ...element.props }, children)
    }

    function LocationValueRender(
        element: React.ReactElement<HTMLSpanElement>,
        value: LocationItem[]
    ) {
        const children = [
            <div className="flex items-center" key={value[0]?.id}>
                <i className="px-2 feather-map-pin text-xs" />
                <div key={1}>
                    {value ? value[0]?.name : ''}
                </div>
            </div>,
        ]

        return cloneElement(element, { ...element.props }, children)
    }

    return (
        <div className='flex w-full px-1 gap-2'>
            <div className="w-[12%]">
                <DropDownList
                    className="bg-white py-1 block w-full shadow-sm border border-gray-300 rounded-md text-sm"
                    iconClassName="feather-chevron-down"
                    data={(AvailabilityOptions.map((item) => ({
                        text: item?.text,
                        value: item?.value,
                    })))}
                    value={AvailabilityOptions.filter((item) => item?.text === override.availability)}
                    textField="text"
                    dataItemKey="value"
                    required={true}
                    valueRender={AvailValueRender}
                    onChange={(e) => {
                        override.availability =
                            e.value.text
                        onAttributesChanged(override, idx)
                    }}
                    popupSettings={{
                        className: "z-[102]"
                    }}
                />
            </div>
            <div className="w-[12%]">
                {locations &&
                    locations.length ? (
                    <DropDownList
                        className="bg-white py-1 block w-full shadow-sm border border-gray-300 rounded-md text-sm"
                        iconClassName="feather-chevron-down"
                        data={location_options}
                        value={location_options.filter((location: LocationItem) => location.id == override.location || (location.id == "" && override.location == undefined))}
                        textField="name"
                        dataItemKey="id"
                        required={true}
                        valueRender={LocationValueRender}
                        onChange={(e) => {
                            override.location = e.value.id
                            override.location_space = undefined
                            onAttributesChanged(override, idx)
                        }}
                        popupSettings={{
                            className: "z-[102]"
                        }}
                    />
                ) : (
                    <div className="bg-white w-full block h-full bg-gray-150 rounded-lg" />
                )}
            </div>
            <div className="w-[12%]">
                <ServiceAvailSelector
                    className="z-10 py-1 w-full bg-white block shadow-sm border border-gray-300 rounded-md text-sm"
                    items={spaces || []}
                    overrideServices={selected_spaces && selected_spaces.length != 0 ? selected_spaces : spaces}
                    title="Space"
                    updateServices={(services: any[]) => {
                        override.location_space = services
                        onAttributesChanged(override, idx)
                    }}
                    update_on_select={false}
                    disabled={override.location == undefined}
                    icon="feather-map-pin"
                />
            </div>
            <div className="w-[12%]">
                <ServiceAvailSelector
                    className="z-10 py-1 bg-white w-full block shadow-sm border border-gray-300 rounded-md text-sm"
                    items={appointments || []}
                    overrideServices={override.services}
                    title="Service"
                    updateServices={(services: any[]) => {
                        override.serviceIds = services
                        onAttributesChanged(override, idx)
                    }}
                    id={idx}
                    update_on_select={false}
                    icon="feather-briefcase"
                />
            </div>
            <div className='w-[20%] flex flex-row gap-2'>
                {/* Extra divs are there because kendo adds another element which breaks the gap */}
                <div>
                    <DateInput
                        minDate={new Date()}
                        className={classNames(
                            'w-full focus:ring-primary-light focus:border-primary-light block shadow-sm border-gray-300 rounded-md calendar text-sm'
                        )}
                        onChange={(date: Date) => {
                            if (date) {
                                override.start_date =
                                    toYmd(date)
                                if (date.getTime() > new Date(override.end_date).getTime()) {
                                    override.end_date =
                                        toYmd(
                                            date
                                        )
                                }
                                override.day_of_week =
                                    DayOfWeek[
                                    date.getDay()
                                    ]
                                onAttributesChanged(override, idx)
                            }
                        }}
                        dateFormat="dd/MM/yyyy"
                        selected={
                            override.start_date &&
                            new Date(
                                override.start_date
                            )
                        }
                    />
                </div>
                <div>
                    <DateInput
                        minDate={new Date(override.start_date)}
                        id="end_date"
                        className="focus:ring-primary-light w-full focus:border-primary-light block shadow-sm border-gray-300 rounded-md calendar text-sm"
                        onChange={(
                            date: Date
                        ) => {
                            if (date) {
                                override.end_date =
                                    toYmd(
                                        date
                                    )
                                onAttributesChanged(override, idx)
                            }

                        }}
                        dateFormat="dd/MM/yyyy"
                        selected={
                            override.end_date &&
                            new Date(
                                override.end_date
                            )
                        }
                    />
                </div>
            </div>
            <div className="w-[32%] flex flex-row gap-2">
                <div className='flex flex-row w-fit h-full items-center text-xs gap-2'>
                    <input
                        type="checkbox"
                        className='w-5 h-5 border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light'
                        onChange={() => {
                            override.all_day = !override.all_day
                            if (override.all_day) {
                                override.from_time = undefined
                                override.to_time = undefined
                            } else {
                                override.from_time = new Date()
                                override.to_time = addHours(1, new Date())
                                if (override.from_time?.getDate() != override.to_time?.getDate()) {
                                    override.to_time = MAX_TIME
                                }
                            }
                            onAttributesChanged(override, idx)
                        }}
                        checked={override.all_day}
                    />
                    <Translation
                        content_key='staff.all_day'
                    />
                </div>
                <TimePicker
                    className={classNames(
                        'focus:ring-primary-light focus:border-primary-light block shadow-sm border-gray-300 rounded-md h-9 calendar text-sm text-inherit disabled:opacity-50',
                        override.all_day ? "!cursor-not-allowed" : "" //need to override via !important
                    )}
                    max={MAX_TIME_RANGE}
                    format="hh:mm a"
                    onChange={(e) => {
                        if (e.target.value) {
                            override.from_time = e.target.value
                            override.to_time = addHours(1, e.target.value)
                            if (override.from_time.getDate() != override.to_time.getDate()) {
                                override.to_time = MAX_TIME
                            }
                            onAttributesChanged(override, idx)
                        }
                    }}
                    value={
                        override.all_day ? new Date() : override.from_time as Date
                    }
                    disabled={override.all_day}
                    popupSettings={{
                        popupClass: "z-[102]"
                    }}
                />
                <TimePicker
                    className={classNames(
                        'focus:ring-primary-light focus:border-primary-light block shadow-sm border-gray-300 rounded-md h-9 calendar text-sm text-inherit disabled:opacity-50',
                        override.all_day ? "!cursor-not-allowed" : "" //need to override via !important
                    )}
                    min={override.from_time || new Date()}
                    format="hh:mm a"
                    onChange={(e) => {
                        if (e.target.value) {
                            override.to_time =
                                e.target.value
                            onAttributesChanged(override, idx)
                        }
                    }}
                    value={
                        override.all_day ? new Date() : override.to_time as Date
                    }
                    disabled={override.all_day}
                    popupSettings={{
                        popupClass: "z-[102]"
                    }}
                />
                <button
                    type="button"
                    className={`text-red-500 bg-red-100/50 h-9 w-9 rounded-lg text-xl feather-archive shrink-0`}
                    onClick={() => {
                        removeHandler(
                            idx,
                            override
                        )
                    }}
                />
            </div>
        </div >
    )
}
